import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Play, RefreshCw, TrendingUp, Link as LinkIcon, Package, Coins, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus, ChevronUp, ChevronDown, Target, ExternalLink, CheckCircle, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ScheduleConfig from '../components/ScheduleConfig';
import HistoryCalendar from '../components/HistoryCalendar';
import PdfDownloadButton from '../components/PdfDownloadButton';
import { toast } from 'sonner';

const AI_ENGINE_COLORS = {
  'ChatGPT': '#10B981',
  'Perplexity': '#22B8CF',
  'Google AI': '#F59E0B',
  'Grok': '#EF4444',
  'Google AI Overviews': '#EC4899',
  'Google AI Mode': '#84CC16',
  'Bing Copilot': '#F97316',
  'You.com': '#7C3AED'
};

const TIME_FILTERS = [
  { labelKey: '7_дней', days: 7 },
  { labelKey: '30_дней', days: 30 },
  { labelKey: '90_дней', days: 90 },
  { labelKey: '1_год', days: 365 }
];

const PRIORITY_STYLES = {
  high: { bg: 'bg-red-500', labelKey: 'высокий' },
  medium: { bg: 'bg-yellow-500', labelKey: 'средний' },
  low: { bg: 'bg-green-500', labelKey: 'низкий' },
};

/* D2: CI Badge */
const CiBadge = ({ ci, className = '' }) => {
  if (!ci || (ci.lower === 0 && ci.upper === 0)) return null;
  return (
    <span className={`text-[10px] text-slate-400 ${className}`}>
      CI [{ci.lower}–{ci.upper}%]
    </span>
  );
};

/* D2: Sentiment Mini Bar */
const SentimentBar = ({ breakdown }) => {
  const { t } = useTranslation();
  if (!breakdown) return null;
  const total = (breakdown.positive || 0) + (breakdown.neutral || 0) + (breakdown.negative || 0);
  if (total === 0) return null;
  const pct = v => Math.round((v / total) * 100);
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('sub.тональность')}</p>
      <div className="flex h-2 w-full overflow-hidden bg-slate-100">
        {breakdown.positive > 0 && <div className="bg-green-400" style={{ width: `${pct(breakdown.positive)}%` }} title={t('sub.позитив', { count: breakdown.positive })} />}
        {breakdown.neutral > 0 && <div className="bg-slate-300" style={{ width: `${pct(breakdown.neutral)}%` }} title={t('sub.нейтрал', { count: breakdown.neutral })} />}
        {breakdown.negative > 0 && <div className="bg-red-400" style={{ width: `${pct(breakdown.negative)}%` }} title={t('sub.негатив', { count: breakdown.negative })} />}
      </div>
      <div className="flex justify-between mt-0.5 text-[9px] text-slate-400">
        <span>+{breakdown.positive}</span>
        <span>~{breakdown.neutral}</span>
        <span>-{breakdown.negative}</span>
      </div>
    </div>
  );
};

/* D2: Position Mini Bar */
const PositionBar = ({ breakdown }) => {
  const { t } = useTranslation();
  if (!breakdown) return null;
  const total = (breakdown.top || 0) + (breakdown.middle || 0) + (breakdown.bottom || 0);
  if (total === 0) return null;
  const pct = v => Math.round((v / total) * 100);
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('sub.позиция')}</p>
      <div className="flex h-2 w-full overflow-hidden bg-slate-100">
        {breakdown.top > 0 && <div className="bg-emerald-500" style={{ width: `${pct(breakdown.top)}%` }} title={t('sub.топ_title', { count: breakdown.top })} />}
        {breakdown.middle > 0 && <div className="bg-amber-400" style={{ width: `${pct(breakdown.middle)}%` }} title={t('sub.середина_title', { count: breakdown.middle })} />}
        {breakdown.bottom > 0 && <div className="bg-slate-400" style={{ width: `${pct(breakdown.bottom)}%` }} title={t('sub.низ_title', { count: breakdown.bottom })} />}
      </div>
      <div className="flex justify-between mt-0.5 text-[9px] text-slate-400">
        <span>{t('sub.топ_краткий', { count: breakdown.top })}</span>
        <span>{t('sub.серед_краткий', { count: breakdown.middle })}</span>
        <span>{t('sub.низ_краткий', { count: breakdown.bottom })}</span>
      </div>
    </div>
  );
};

const SubMonitorDetails = () => {
  const { monitorId, submonitorId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const [submonitor, setSubmonitor] = useState(null);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [timeFilter, setTimeFilter] = useState(30);
  const [chartMetric, setChartMetric] = useState('visibility');
  const [creditBalance, setCreditBalance] = useState(null);
  const [opCosts, setOpCosts] = useState({});
  const [customQueries, setCustomQueries] = useState([]);
  const [newQuery, setNewQuery] = useState('');
  const [expandedRec, setExpandedRec] = useState(null);
  const [selectedQueryRanking, setSelectedQueryRanking] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadData();
    loadBillingInfo();
    const interval = setInterval(() => {
      if (submonitor?.status === 'analyzing') {
        loadData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [submonitorId, submonitor?.status, timeFilter]);

  const loadBillingInfo = async () => {
    try {
      const [balRes, costRes] = await Promise.all([
        axios.get(`${API_URL}/api/billing/balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/billing/operation-costs`, { withCredentials: true }),
      ]);
      setCreditBalance(balRes.data.balance);
      setOpCosts(costRes.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (submonitor?.status === 'analyzing') {
        loadData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [submonitorId, submonitor?.status, timeFilter]);

  const loadData = async () => {
    try {
      const [subRes, resultsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/submonitors/${submonitorId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/submonitors/${submonitorId}/results`, { withCredentials: true }),
        axios.get(`${API_URL}/api/submonitors/${submonitorId}/history?days=${timeFilter}`, { withCredentials: true })
      ]);
      setSubmonitor(subRes.data);
      setResults(resultsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error loading submonitor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const rankingsMap = React.useMemo(() => {
    const map = {};
    const rankingsData = results?.query_rankings || results?.brand_rankings;
    if (rankingsData) {
      rankingsData.forEach(r => {
        const key = r.query;
        if (!map[key]) map[key] = {};
        map[key][r.engine] = {
          target_position: r.target_position,
          total_brands: r.total_brands,
          brands: r.brands || []
        };
      });
    }
    return map;
  }, [results]);

  const handleStartAnalysis = async () => {
    setAnalyzing(true);
    setSubmonitor(prev => prev ? { ...prev, status: 'analyzing' } : prev);
    toast.info(t('sub.анализ_запущен'));
    try {
      await axios.post(
        `${API_URL}/api/submonitors/${submonitorId}/analyze`,
        {},
        { withCredentials: true }
      );
      loadBillingInfo();
      setTimeout(() => loadData(), 2000);
    } catch (error) {
      loadData();
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail || t('sub.недостаточно_кредитов'));
      } else {
        toast.error(t('sub.ошибка_запуска'));
        console.error('Error starting product analysis:', error);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddQuery = async () => {
    const q = newQuery.trim();
    if (!q) return;
    const updated = [...customQueries, q];
    setCustomQueries(updated);
    setNewQuery('');
    try {
      await axios.put(`${API_URL}/api/submonitors/${submonitorId}/custom-queries`, { queries: updated });
    } catch (err) { toast.error(t('sub.ошибка_сохранения_запроса')); }
  };
  const handleRemoveQuery = async (idx) => {
    const updated = customQueries.filter((_, i) => i !== idx);
    setCustomQueries(updated);
    try {
      await axios.put(`${API_URL}/api/submonitors/${submonitorId}/custom-queries`, { queries: updated });
    } catch (err) { toast.error(t('sub.ошибка_удаления_запроса')); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]">
        <div className="text-2xl font-bold text-[#0F172A]">{t('common.загрузка')}</div>
      </div>
    );
  }

  if (!submonitor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]">
        <div className="text-2xl font-bold text-red-600">{t('sub.не_найден')}</div>
      </div>
    );
  }

  const chartData = history.map(h => {
    const dataPoint = {
      date: new Date(h.created_at).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' }),
      sov: h.sov_overall
    };
    Object.entries(h.ai_engine_results || {}).forEach(([engine, data]) => {
      if (chartMetric === 'visibility') {
        dataPoint[engine] = data.visibility_percent;
      } else {
        dataPoint[`${engine}_citation`] = data.citation_percent;
      }
    });
    return dataPoint;
  });

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#1BB594] hover:underline font-semibold"
            data-testid="back-to-monitor-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-baseline gap-1.5">
            <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
            <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
            <span className="hidden sm:inline text-[10px] text-slate-400 ml-2">{t('sub.сервис_слоган')}</span>
          </div>
        </div>
      </header>

      <main id="sub-pdf-content" className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="mb-12 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-6 h-6 text-[#1BB594]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1BB594] bg-blue-50 px-3 py-1">
                {t('sub.товар_услуга')}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-black text-[#2F2E2F] mb-4" data-testid="submonitor-title">
              {submonitor.product_name}
            </h1>
            <div className="flex items-center gap-4 text-base text-slate-600">
              <span>{submonitor.parent_company_info?.company_name}</span>
              <span className="text-slate-400">·</span>
              <span>{submonitor.parent_country}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {results && (
              <PdfDownloadButton targetId="sub-pdf-content" filename={`product-${submonitor.product_name}`} />
            )}
            {submonitor.status === 'completed' && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleStartAnalysis}
                  disabled={analyzing || (opCosts.sub_monitor > 0 && creditBalance !== null && creditBalance < opCosts.sub_monitor)}
                  className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-3 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50"
                  data-testid="rerun-submonitor-button"
                >
                  <RefreshCw className={`w-4 h-4 ${analyzing ? 'animate-spin' : ''}`} />
                  {analyzing ? t('sub.запуск') : t('sub.мониторинг_товара')}
                </button>
                {opCosts.sub_monitor > 0 && (
                  <span className={`text-xs flex items-center gap-1 ${creditBalance !== null && creditBalance < opCosts.sub_monitor ? 'text-red-500' : 'text-slate-500'}`}>
                    <Coins className="w-3 h-3" /> ${(opCosts.sub_monitor / 100).toFixed(2)} (balance: {creditBalance != null ? '$' + (creditBalance / 100).toFixed(2) : '...'})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {submonitor.status === 'pending' && (
          <div className="mb-12 p-8 bg-blue-50 border border-blue-200" data-testid="start-subanalysis-prompt">
            <h3 className="text-xl font-bold text-blue-900 mb-4">{t('sub.запустить_анализ_вопрос')}</h3>
            <p className="text-blue-800 mb-6">
              {t('sub.запустить_анализ_описание', { product: submonitor.product_name })}
            </p>
            <button
              onClick={handleStartAnalysis}
              disabled={analyzing || (opCosts.sub_monitor > 0 && creditBalance !== null && creditBalance < opCosts.sub_monitor)}
              className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-4 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50"
              data-testid="start-subanalysis-button"
            >
              <Play className="w-5 h-5" />
              {analyzing ? t('sub.запуск') : t('sub.запустить_анализ')}
            </button>
            {opCosts.sub_monitor > 0 && (
              <p className={`text-xs mt-2 flex items-center gap-1 ${creditBalance !== null && creditBalance < opCosts.sub_monitor ? 'text-red-500' : 'text-blue-700'}`}>
                <Coins className="w-3 h-3" /> ${(opCosts.sub_monitor / 100).toFixed(2)} (balance: {creditBalance != null ? '$' + (creditBalance / 100).toFixed(2) : '...'})
              </p>
            )}
          </div>
        )}

        {submonitor.status === 'analyzing' && (
          <div className="mb-12 p-8 bg-blue-50 border border-blue-200 flex items-center gap-4" data-testid="subanalyzing-status">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
            <div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">{t('sub.анализ_в_процессе')}</h3>
              <p className="text-blue-800">{t('sub.анализируем_видимость', { product: submonitor.product_name })}</p>
            </div>
          </div>
        )}

        {submonitor.status === 'error' && (
          <div className="mb-12 p-8 bg-red-50 border border-red-300" data-testid="sub-error-status">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-900 mb-2">{t('sub.ошибка_анализа')}</h3>
                {submonitor.error_code && (
                  <div className="mb-3 flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold tracking-widest uppercase">
                      {submonitor.error_code}
                    </span>
                  </div>
                )}
                <p className="text-red-700 mb-4">
                  {submonitor.error_message || t('sub.неизвестная_ошибка')}
                </p>
                <button
                  onClick={handleStartAnalysis}
                  disabled={analyzing || (opCosts.sub_monitor > 0 && creditBalance !== null && creditBalance < opCosts.sub_monitor)}
                  className="rounded-none font-bold tracking-widest uppercase text-xs px-8 py-4 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50"
                  data-testid="retry-subanalysis-button"
                >
                  <RefreshCw className={`w-5 h-5 ${analyzing ? 'animate-spin' : ''}`} />
                  {analyzing ? t('sub.запуск') : t('sub.повторить_анализ')}
                </button>
                {opCosts.sub_monitor > 0 && (
                  <p className={`text-xs mt-2 flex items-center gap-1 ${creditBalance !== null && creditBalance < opCosts.sub_monitor ? 'text-red-500' : 'text-red-600'}`}>
                    <Coins className="w-3 h-3" /> ${(opCosts.sub_monitor / 100).toFixed(2)} (balance: {creditBalance != null ? '$' + (creditBalance / 100).toFixed(2) : '...'})
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {submonitor.status === 'completed' && (
          <ScheduleConfig targetId={submonitorId} targetType="product" />
        )}

        {results && (() => {
          // ── Recompute aggregate metrics from per-engine data ──
          const _seArr = Object.entries(results.ai_engine_results || {}).filter(([e]) => e !== 'DeepSeek');
          const _seCount = _seArr.length;
          const _sr = _seCount > 0 ? (() => {
            const avg = (fn) => +(_seArr.reduce((s, [, d]) => s + (fn(d) || 0), 0) / _seCount).toFixed(2);
            const vis = avg(d => d.visibility_percent);
            const cit = avg(d => d.citation_percent);
            return { mkt: avg(d => d.market_visibility), brd: avg(d => d.brand_visibility), vis, cit, overall: +(0.6 * vis + 0.4 * cit).toFixed(2) };
          })() : null;
          return (<>
            {/* D2: Key Changes Banner */}
            {(results.key_changes || []).length > 0 && (
              <div className="mb-8 flex flex-wrap gap-3" data-testid="sub-key-changes">
                {results.key_changes.map((change, idx) => (
                  <div key={idx} className={`inline-flex items-center gap-2 px-4 py-2 border text-sm font-medium ${
                    change.direction === 'up' ? 'bg-green-50 border-green-300 text-green-800' :
                    change.direction === 'down' ? 'bg-red-50 border-red-300 text-red-800' :
                    'bg-slate-50 border-slate-300 text-slate-700'
                  }`}>
                    {change.direction === 'up' ? <ArrowUpRight className="w-4 h-4" /> :
                     change.direction === 'down' ? <ArrowDownRight className="w-4 h-4" /> :
                     <Minus className="w-4 h-4" />}
                    {change.text}
                  </div>
                ))}
              </div>
            )}

            <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sub-sov-scorecard">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center md:border-r border-[#E2DDD8]">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">MARKET SOV</p>
                  <p className="text-xs text-slate-400 mb-2">{t('sub.конкурентная_видимость')}</p>
                  <div className="text-5xl sm:text-6xl tracking-tighter font-black text-[#1BB594]" data-testid="sub-market-sov">
                    {_sr?.mkt ?? results.market_sov ?? '—'}%
                  </div>
                  <CiBadge ci={results.ci_intervals?.market_sov} className="mt-1" />
                </div>
                <div className="text-center md:border-r border-[#E2DDD8]">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">BRAND SOV</p>
                  <p className="text-xs text-slate-400 mb-2">{t('sub.узнаваемость')}</p>
                  <div className="text-5xl sm:text-6xl tracking-tighter font-black text-emerald-600" data-testid="sub-brand-sov">
                    {_sr?.brd ?? results.brand_sov ?? '—'}%
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{t('sub.общий_sov')}</p>
                  <p className="text-xs text-slate-400 mb-2">{submonitor.product_name}</p>
                  <div className="text-5xl sm:text-6xl tracking-tighter font-black text-slate-400" data-testid="sub-total-sov">
                    {_sr?.overall ?? results.sov_overall}%
                  </div>
                  <CiBadge ci={results.ci_intervals?.sov_overall} className="mt-1" />
                </div>
              </div>
              {/* D2: Sub-metrics row */}
              <div className="mt-6 pt-4 border-t border-[#E2DDD8] flex flex-wrap gap-6 justify-center text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('sub.видимость')}</p>
                  <p className="text-lg font-bold text-slate-700">{_sr?.vis ?? results.visibility_global ?? '—'}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('sub.цитируемость')}</p>
                  <p className="text-lg font-bold text-slate-700">{_sr?.cit ?? results.citation_global ?? '—'}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('sub.движков')}</p>
                  <p className="text-lg font-bold text-slate-700">{_seCount}/{_seCount}</p>
                </div>
                {results.monitoring_meta?.duration_seconds && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('sub.время')}</p>
                    <p className="text-lg font-bold text-slate-700">{Math.round(results.monitoring_meta.duration_seconds)}{t('sub.сек')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('sub.результаты_ai')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0" data-testid="sub-ai-engines-grid">
                {Object.entries(results.ai_engine_results || {}).map(([engine, data]) => (
                  engine === 'DeepSeek' ? (
                    <div key="arpai-placeholder" className="bg-white border border-[#E2DDD8] p-6 flex items-center justify-center" style={{ minHeight: 220 }}>
                      <img src="/arpai_icon.png" alt="ARPAI GEO" style={{ maxWidth: '60%', maxHeight: '70%', objectFit: 'contain', opacity: 0.15 }} />
                    </div>
                  ) : (
                  <div key={engine} className="bg-white border border-[#E2DDD8] p-6" data-testid={`sub-engine-card-${engine}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3" style={{ backgroundColor: AI_ENGINE_COLORS[engine] }}></div>
                      <h3 className="text-lg font-bold text-slate-900">{engine}</h3>
                      {data.active === false && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5">{t('sub.неактивен')}</span>}
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500">Market</p>
                          <p className="text-2xl font-black text-[#1BB594]">{data.market_visibility ?? data.visibility_percent}%</p>
                          <p className="text-[10px] text-slate-400">{data.market_mentions ?? '?'}/{data.market_total ?? '?'}</p>
                          <CiBadge ci={data.ci?.market_visibility} />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500">Brand</p>
                          <p className="text-2xl font-black text-emerald-600">{data.brand_visibility ?? data.visibility_percent}%</p>
                          <p className="text-[10px] text-slate-400">{data.brand_q_mentions ?? '?'}/{data.brand_q_total ?? '?'}</p>
                          <CiBadge ci={data.ci?.brand_visibility} />
                        </div>
                      </div>
                      <div className="pt-2 border-t border-slate-200">
                        <div className="flex justify-between items-baseline">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                              <TrendingUp className="w-3 h-3" /> {t('sub.видимость')}
                            </p>
                            <p className="text-xl font-black text-[#2F2E2F]">{data.visibility_percent}%</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1 justify-end">
                              <LinkIcon className="w-3 h-3" /> {t('sub.цитируемость')}
                            </p>
                            <p className="text-xl font-black text-[#2F2E2F]">{data.citation_percent ?? 0}%</p>
                          </div>
                        </div>
                        <CiBadge ci={data.ci?.visibility} />
                      </div>
                      <SentimentBar breakdown={data.sentiment_breakdown} />
                      <PositionBar breakdown={data.position_breakdown} />
                    </div>
                  </div>
                  )
                ))}
              </div>
            </div>

            {results.queries_generated && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sub-queries-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('sub.запросы_по_товару')}</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">{t('sub.без_бренда')}</p>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {results.queries_generated.non_brand?.map((query, idx) => {
                        const engines = results.query_engine_map?.[query] || [];
                        const qRankings = rankingsMap[query] || {};
                        const engKeys = Object.keys(qRankings).length > 0 ? Object.keys(qRankings) : engines;
                        const targetPositions = Object.values(qRankings).map(r => r.target_position).filter(p => p != null);
                        const bestPos = targetPositions.length > 0 ? Math.min(...targetPositions) : null;
                        const hasRankData = Object.keys(qRankings).length > 0;
                        const hasBrands = Object.values(qRankings).some(r => r.total_brands > 0);
                        const borderColor = bestPos != null ? (bestPos <= 2 ? '#10B981' : bestPos <= 5 ? '#F59E0B' : '#EF4444') : (hasRankData ? (hasBrands ? '#F59E0B' : '#6B7280') : '#94A3B8');
                        const bgColor = bestPos != null ? (bestPos <= 2 ? '#ECFDF5' : bestPos <= 5 ? '#FFFBEB' : '#FEF2F2') : (hasRankData ? (hasBrands ? '#FFFBEB' : '#F3F4F6') : '#F8FAFC');
                        return (
                          <div key={idx} className="p-2 cursor-pointer hover:shadow-md transition-shadow"
                               style={{ backgroundColor: bgColor, borderLeft: `3px solid ${borderColor}` }}
                               onClick={() => setSelectedQueryRanking({ query, engines: engKeys, rankings: qRankings })}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-900 flex-1">{query}</p>
                              {bestPos ? <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: borderColor }}>#{bestPos}</span> : hasRankData ? (hasBrands ? <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700">{t('geo.нет_в_топ')}</span> : <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600">{t('geo.ложный')}</span>) : null}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {engKeys.map(eng => {
                                const r = qRankings[eng];
                                const pos = r?.target_position;
                                const posColor = pos != null ? (pos <= 2 ? '#10B981' : pos <= 5 ? '#F59E0B' : '#EF4444') : null;
                                return (
                                  <span key={eng} className="text-[10px] font-bold px-1.5 py-0.5 inline-flex items-center gap-1" style={{ backgroundColor: `${AI_ENGINE_COLORS[eng] || '#94A3B8'}20`, color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>
                                    {eng}
                                    {pos != null && <span className="rounded px-1 text-white" style={{ backgroundColor: posColor }}>#{pos}</span>}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">{t('sub.с_брендом')}</p>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {results.queries_generated.brand?.map((query, idx) => {
                        const engines = results.query_engine_map?.[query] || [];
                        const qRankings = rankingsMap[query] || {};
                        const engKeys = Object.keys(qRankings).length > 0 ? Object.keys(qRankings) : engines;
                        const targetPositions = Object.values(qRankings).map(r => r.target_position).filter(p => p != null);
                        const bestPos = targetPositions.length > 0 ? Math.min(...targetPositions) : null;
                        const hasRankData = Object.keys(qRankings).length > 0;
                        const hasBrands = Object.values(qRankings).some(r => r.total_brands > 0);
                        const borderColor = bestPos != null ? (bestPos <= 2 ? '#10B981' : bestPos <= 5 ? '#F59E0B' : '#EF4444') : (hasRankData ? (hasBrands ? '#F59E0B' : '#6B7280') : '#94A3B8');
                        const bgColor = bestPos != null ? (bestPos <= 2 ? '#ECFDF5' : bestPos <= 5 ? '#FFFBEB' : '#FEF2F2') : (hasRankData ? (hasBrands ? '#FFFBEB' : '#F3F4F6') : '#F8FAFC');
                        return (
                          <div key={idx} className="p-2 cursor-pointer hover:shadow-md transition-shadow"
                               style={{ backgroundColor: bgColor, borderLeft: `3px solid ${borderColor}` }}
                               onClick={() => setSelectedQueryRanking({ query, engines: engKeys, rankings: qRankings })}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-900 flex-1">{query}</p>
                              {bestPos ? <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: borderColor }}>#{bestPos}</span> : hasRankData ? (hasBrands ? <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700">{t('geo.нет_в_топ')}</span> : <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600">{t('geo.ложный')}</span>) : null}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {engKeys.map(eng => {
                                const r = qRankings[eng];
                                const pos = r?.target_position;
                                const posColor = pos != null ? (pos <= 2 ? '#10B981' : pos <= 5 ? '#F59E0B' : '#EF4444') : null;
                                return (
                                  <span key={eng} className="text-[10px] font-bold px-1.5 py-0.5 inline-flex items-center gap-1" style={{ backgroundColor: `${AI_ENGINE_COLORS[eng] || '#94A3B8'}20`, color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>
                                    {eng}
                                    {pos != null && <span className="rounded px-1 text-white" style={{ backgroundColor: posColor }}>#{pos}</span>}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {results.queries_generated.custom?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">{t('sub.свои_запросы_мониторинг')}</p>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {results.queries_generated.custom.map((query, idx) => {
                        const engines = results.query_engine_map?.[query] || [];
                        const qRankings = rankingsMap[query] || {};
                        const engKeys = Object.keys(qRankings).length > 0 ? Object.keys(qRankings) : engines;
                        const targetPositions = Object.values(qRankings).map(r => r.target_position).filter(p => p != null);
                        const bestPos = targetPositions.length > 0 ? Math.min(...targetPositions) : null;
                        const hasRankData = Object.keys(qRankings).length > 0;
                        const hasBrands = Object.values(qRankings).some(r => r.total_brands > 0);
                        const borderColor = bestPos != null ? (bestPos <= 2 ? '#10B981' : bestPos <= 5 ? '#F59E0B' : '#EF4444') : (hasRankData ? (hasBrands ? '#F59E0B' : '#6B7280') : '#94A3B8');
                        const bgColor = bestPos != null ? (bestPos <= 2 ? '#ECFDF5' : bestPos <= 5 ? '#FFFBEB' : '#FEF2F2') : (hasRankData ? (hasBrands ? '#FFFBEB' : '#F3F4F6') : '#F8FAFC');
                        return (
                          <div key={idx} className="p-2 cursor-pointer hover:shadow-md transition-shadow"
                               style={{ backgroundColor: bgColor, borderLeft: `3px solid ${borderColor}` }}
                               onClick={() => setSelectedQueryRanking({ query, engines: engKeys, rankings: qRankings })}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-slate-900 flex-1">{query}</p>
                              {bestPos ? <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded text-white" style={{ backgroundColor: borderColor }}>#{bestPos}</span> : null}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {engKeys.map(eng => {
                                const r = qRankings[eng];
                                const pos = r?.target_position;
                                const posColor = pos != null ? (pos <= 2 ? '#10B981' : pos <= 5 ? '#F59E0B' : '#EF4444') : null;
                                return (
                                  <span key={eng} className="text-[10px] font-bold px-1.5 py-0.5 inline-flex items-center gap-1" style={{ backgroundColor: `${AI_ENGINE_COLORS[eng] || '#94A3B8'}20`, color: AI_ENGINE_COLORS[eng] || '#94A3B8' }}>
                                    {eng}
                                    {pos != null && <span className="rounded px-1 text-white" style={{ backgroundColor: posColor }}>#{pos}</span>}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}


            <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-4">{t('sub.свои_запросы')}</h2>
              <p className="text-sm text-slate-500 mb-4">{t('sub.свои_запросы_описание')}</p>
              <div className="space-y-2 mb-4">
                {customQueries.map((q, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 border border-[#E2DDD8]">
                    <span className="text-sm text-slate-800">{q}</span>
                    <button onClick={() => handleRemoveQuery(idx)} className="text-slate-400 hover:text-red-500 text-xs ml-2">{"\u2715"}</button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={newQuery} onChange={(e) => setNewQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddQuery()} placeholder={t('sub.ph_запрос')} className="flex-1 px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]" />
                <button onClick={handleAddQuery} disabled={!newQuery.trim()} className="font-bold tracking-widest uppercase text-xs px-5 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">{"\u2295"} {t('sub.добавить')}</button>
              </div>
            </div>

            {results.competitor_analysis && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sub-competitor-section">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-8">{t('sub.конкуренты_по_товару')}</h2>
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>

                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">

                      <TrendingUp className="w-5 h-5 text-[#1BB594]" />

                      {results.product_competitors?.length > 0 ? t('sub.конкурирующие_товары') : t('sub.топ_10_брендов')}

                    </h3>

                    {results.product_competitors?.length > 0 ? (

                      <div className="space-y-3">

                        {results.product_competitors.map((pc, idx) => (

                          <div key={idx} className="flex justify-between items-start p-3 bg-slate-50 border border-slate-200">

                            <div className="flex items-start gap-3">

                              <span className="text-lg font-black text-[#1BB594] w-8 flex-shrink-0">{idx + 1}</span>

                              <div className="min-w-0">

                                <div className="flex items-center gap-2">

                                  <p className="font-semibold text-slate-900">{pc.product_name}</p>

                                  {pc.url_verified && <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}

                                </div>

                                <p className="text-xs text-slate-500">{pc.brand}{pc.price ? ` · ${pc.price}` : ''}</p>

                                {pc.product_url && (

                                  <a href={pc.product_url} target="_blank" rel="noopener noreferrer"

                                     className="text-xs text-[#1BB594] hover:underline flex items-center gap-1 mt-0.5">

                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />

                                    <span className="truncate">{pc.product_url.replace(/^https?:\/\//, '').substring(0, 55)}</span>

                                  </a>

                                )}

                                {pc.reason && <p className="text-xs text-slate-400 mt-0.5 italic">{pc.reason}</p>}

                              </div>

                            </div>

                            <span className="text-sm font-bold text-slate-600 flex-shrink-0 ml-2">{pc.mentions} {t('sub.упом')}</span>

                          </div>

                        ))}

                      </div>

                    ) : results.competitor_analysis?.top_competitors?.length > 0 ? (

                      <div className="space-y-3">

                        {results.competitor_analysis.top_competitors.map((competitor, idx) => (

                          <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200">

                            <div className="flex items-center gap-3">

                              <span className="text-lg font-black text-[#1BB594] w-8">{idx + 1}</span>

                              <div>

                                <p className="font-semibold text-slate-900">{competitor.brand}</p>

                                {competitor.category && <p className="text-xs text-slate-500">{competitor.category}</p>}

                              </div>

                            </div>

                            <span className="text-sm font-bold text-slate-600">{competitor.mentions} {t('sub.упоминаний')}</span>

                          </div>

                        ))}

                      </div>

                    ) : (

                      <p className="text-slate-500">{t('sub.нет_конкурентов')}</p>

                    )}

                  </div>


                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-[#1BB594]" />
                      {t('sub.релевантность')}
                    </h3>
                    {results.competitor_analysis.query_relevance?.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {results.competitor_analysis.query_relevance.slice(0, 10).map((query, idx) => (
                          <div key={idx} className="p-2 bg-slate-50 border border-slate-200">
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-sm text-slate-900 flex-1">{query.query}</p>
                              <span className="text-sm font-bold text-[#1BB594] ml-2">{query.relevance_score}%</span>
                            </div>
                            <p className="text-xs text-slate-500">{t('sub.упоминаний_из', { mentions: query.mentions, total: query.total_results })}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500">{t('sub.нет_данных')}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4">{t('sub.топ_20_ресурсов')}</h3>
                  {results.competitor_analysis.top_resources?.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {results.competitor_analysis.top_resources.map((resource, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 border border-slate-200">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-xs font-bold text-slate-400 mr-2">#{idx + 1}</span>
                            <span className="text-xs font-bold text-[#1BB594]">{resource.mentions}</span>
                          </div>
                          <p className="text-sm font-semibold text-slate-900 mb-1 break-all">{resource.domain}</p>
                          <p className="text-xs text-slate-500">{resource.type}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">{t('sub.нет_данных')}</p>
                  )}
                </div>
              </div>
            )}

            {/* D2: Recommendations */}
            {(results.recommendations || []).length > 0 && (
              <div className="mb-12 bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sub-recommendations">
                <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F] mb-6">{t('sub.рекомендации')}</h2>
                <div className="space-y-3">
                  {results.recommendations.map((rec, idx) => {
                    const isExpanded = expandedRec === idx;
                    const pstyle = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium;
                    return (
                      <div key={idx} className="border border-slate-200 bg-slate-50">
                        <div className="p-4 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedRec(isExpanded ? null : idx)}>
                          <span className={`px-2 py-1 text-xs font-bold uppercase text-white ${pstyle.bg}`}>
                            {t('sub.' + pstyle.labelKey)}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-900">{rec.title}</p>
                            <div className="flex gap-2 mt-1 flex-wrap">
                              {(rec.engines || []).map(e => (
                                <span key={e} className="text-[10px] font-bold px-1.5 py-0.5" style={{ backgroundColor: `${AI_ENGINE_COLORS[e]}20`, color: AI_ENGINE_COLORS[e] }}>
                                  {e}
                                </span>
                              ))}
                              {rec.category && <span className="text-[10px] px-1.5 py-0.5 bg-slate-200">{rec.category}</span>}
                            </div>
                          </div>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-slate-200 pt-3">
                            {rec.fact && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('sub.факт')}</span> {rec.fact}</p>}
                            {rec.gap && <p className="text-xs text-slate-600 mb-2"><span className="font-bold">{t('sub.разрыв')}</span> {rec.gap}</p>}
                            {rec.hypothesis && <p className="text-xs text-slate-700 mb-3 italic">"{rec.hypothesis}"</p>}
                            {rec.tasks?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('sub.задачи')}</p>
                                {rec.tasks.map((t, ti) => <p key={ti} className="text-xs text-slate-600 ml-2">• {t.text || t}</p>)}
                              </div>
                            )}
                            {rec.verification && (
                              <p className="text-[10px] text-slate-400">
                                <Target className="w-3 h-3 inline mr-1" />
                                {t('sub.проверка', { signal: rec.verification.expected_signal, days: rec.verification.recommended_recheck_days })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {history.length > 0 && (
              <HistoryCalendar
                history={history}
                onSelectSnapshot={(snapshot, date) => {
                  console.log('Selected snapshot:', date, snapshot);
                }}
              />
            )}

            {chartData.length >= 1 && (
              <div className="bg-white border border-[#E2DDD8] p-6 sm:p-8" data-testid="sub-history-chart">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2F2E2F]">{t('sub.динамика')}</h2>
                  <div className="flex gap-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setChartMetric('visibility')}
                        className={`rounded-none font-bold tracking-widest uppercase text-xs px-4 py-2 border transition-colors ${
                          chartMetric === 'visibility'
                            ? 'bg-[#1BB594] text-white border-[#1BB594]'
                            : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'
                        }`}
                        data-testid="sub-visibility-chart-button"
                      >
                        <TrendingUp className="w-4 h-4 inline mr-1" />
                        {t('sub.видимость')}
                      </button>
                      <button
                        onClick={() => setChartMetric('citation')}
                        className={`rounded-none font-bold tracking-widest uppercase text-xs px-4 py-2 border transition-colors ${
                          chartMetric === 'citation'
                            ? 'bg-[#1BB594] text-white border-[#1BB594]'
                            : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'
                        }`}
                        data-testid="sub-citation-chart-button"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        {t('sub.цитируемость')}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      {TIME_FILTERS.map(filter => (
                        <button
                          key={filter.days}
                          onClick={() => setTimeFilter(filter.days)}
                          className={`rounded-none font-bold tracking-widest uppercase text-xs px-3 py-2 border transition-colors ${
                            timeFilter === filter.days
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'
                          }`}
                          data-testid={`sub-time-filter-${filter.days}`}
                        >
                          {t('sub.' + filter.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData} accessibilityLayer>
                    <CartesianGrid strokeDasharray="0" stroke="#E2DDD8" opacity={0.1} />
                    <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #E2DDD8',
                        borderRadius: 0,
                        boxShadow: 'none'
                      }}
                    />
                    <Legend />
                    {Object.keys(AI_ENGINE_COLORS).filter(e => e !== 'DeepSeek').map(engine => (
                      <Line
                        key={engine}
                        type="monotone"
                        dataKey={chartMetric === 'visibility' ? engine : `${engine}_citation`}
                        name={engine}
                        stroke={AI_ENGINE_COLORS[engine]}
                        strokeWidth={2}
                        dot={{ r: 3, fill: AI_ENGINE_COLORS[engine] }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Brand Rankings Modal */}
            {selectedQueryRanking && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedQueryRanking(null)}>
                <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-xl border border-[#E2DDD8]" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#2F2E2F]">{t('geo.рейтинг_брендов')}</h3>
                    <button onClick={() => setSelectedQueryRanking(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                  </div>
                  <p className="text-sm text-slate-700 mb-4 p-3 bg-slate-50 border-l-2 border-[#1BB594]">{selectedQueryRanking.query}</p>
                  <div className="space-y-3">
                    {selectedQueryRanking.engines.map(eng => {
                      const r = selectedQueryRanking.rankings[eng];
                      if (!r) return (
                        <div key={eng} className="p-3 bg-slate-50 border border-slate-200">
                          <span className="text-sm font-bold" style={{ color: AI_ENGINE_COLORS[eng] }}>{eng}</span>
                          <span className="text-xs text-slate-400 italic ml-2">{t('geo.нет_данных_рейтинг')}</span>
                        </div>
                      );
                      const posColor = r.target_position ? (r.target_position <= 2 ? '#10B981' : r.target_position <= 5 ? '#F59E0B' : '#EF4444') : (r.total_brands > 0 ? '#F59E0B' : '#6B7280');
                      return (
                        <div key={eng} className="p-3 bg-white border border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold" style={{ color: AI_ENGINE_COLORS[eng] }}>{eng}</span>
                            <span className="text-sm font-bold" style={{ color: posColor }}>
                              {r.target_position ? t('geo.позиция_из', { pos: r.target_position, total: r.total_brands }) : (r.total_brands > 0 ? t('geo.нет_в_топ_n', { n: r.total_brands }) : t('geo.ложный_запрос'))}
                            </span>
                          </div>
                          {r.brands && r.brands.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {r.brands.map((b, i) => (
                                <span key={i} className={`text-[11px] px-2 py-0.5 border ${b.is_target ? 'bg-emerald-50 border-emerald-300 font-bold text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                  {b.position}. {b.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>);
        })()}
      </main>
    </div>
  );
};

export default SubMonitorDetails;
